import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { supportedLanguages, getEffectiveLang } from "@/i18n";
import i18n from "@/i18n";
import {
  regions,
  globalRegion,
  findRegionByCountryCode,
  getDefaultRegionForUnknownCountry,
  getRegionsByContinent,
  continentOrder,
  continentNames,
  langLabelMap,
  type RegionData,
} from "@/mocks/regions";

interface RegionSelectorProps {
  onComplete: () => void;
  forceOpen?: boolean;
}

// Geolocation providers, tried in order. The first one that answers with a valid
// ISO-3166 alpha-2 country code wins, so a single blocked/rate-limited service
// no longer leaves the visitor stuck on the "Global" default.
const IP_LOOKUP_PROVIDERS: { url: string; pick: (data: unknown) => string | undefined }[] = [
  { url: "https://ipwho.is/", pick: (data) => (data as { country_code?: string })?.country_code },
  { url: "https://ipapi.co/json/", pick: (data) => (data as { country_code?: string })?.country_code },
  { url: "https://ipwhois.app/json/", pick: (data) => (data as { country_code?: string })?.country_code },
];

async function fetchCountryFromProvider(
  provider: (typeof IP_LOOKUP_PROVIDERS)[number]
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2500);
  try {
    const res = await fetch(provider.url, { signal: controller.signal });
    if (!res.ok) return null;
    const data = await res.json();
    const code = provider.pick(data);
    if (typeof code === "string" && /^[A-Za-z]{2}$/.test(code)) return code.toUpperCase();
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Secondary signal: infer the country from the browser locale (e.g. "es-PE" → PE).
function getCountryFromLocale(): string | null {
  const candidates = [navigator.language, ...(navigator.languages || [])];
  for (const locale of candidates) {
    if (!locale) continue;
    const match = locale.match(/[-_]([A-Za-z]{2})\b/);
    if (match) return match[1].toUpperCase();
  }
  return null;
}

async function detectVisitorCountryCode(): Promise<string | null> {
  for (const provider of IP_LOOKUP_PROVIDERS) {
    const code = await fetchCountryFromProvider(provider);
    if (code) return code;
  }
  return getCountryFromLocale();
}

export default function RegionSelector({ onComplete, forceOpen }: RegionSelectorProps) {
  const { t } = useTranslation("common");
  const [phase, setPhase] = useState<"detecting" | "selecting" | "done">("detecting");
  const [detected, setDetected] = useState<RegionData | null>(null);
  const [selected, setSelected] = useState<RegionData | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const hasClosed = useRef(false);
  const initGuard = useRef(false);

  // Lock body scroll and allow ESC to close the region picker modal
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  // forceOpen: reopens the full modal from navbar
  useEffect(() => {
    if (!forceOpen) return;
    hasClosed.current = false;
    initGuard.current = true;
    const saved = sessionStorage.getItem("openlab-region");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const region = regions.find((r) => r.code === parsed.code);
        if (region) {
          setDetected(region);
          setSelected(region);
        } else {
          setDetected(globalRegion);
          setSelected(globalRegion);
        }
      } catch {
        setDetected(globalRegion);
        setSelected(globalRegion);
      }
    } else {
      setDetected(globalRegion);
      setSelected(globalRegion);
    }
    setPhase("selecting");
  }, [forceOpen]);

  // Initial detection on first visit per browser session
  useEffect(() => {
    if (forceOpen || initGuard.current) return;
    initGuard.current = true;

    const saved = sessionStorage.getItem("openlab-region");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const region = regions.find((r) => r.code === parsed.code);
        if (region) {
          const effectiveLang = getEffectiveLang(region.lang);
          i18n.changeLanguage(effectiveLang);
          setPhase("done");
          onComplete();
          return;
        }
      } catch {
        // ignore, proceed to detection
      }
    }

    detectVisitorCountryCode()
      .then((countryCode) => {
        if (!(initGuard.current && !hasClosed.current)) return;
        if (countryCode) {
          const region = findRegionByCountryCode(countryCode);
          const resolved = region || getDefaultRegionForUnknownCountry(countryCode);
          setDetected(resolved);
          setSelected(resolved);
        } else {
          setDetected(globalRegion);
          setSelected(globalRegion);
        }
      })
      .catch(() => {
        if (initGuard.current && !hasClosed.current) {
          setDetected(globalRegion);
          setSelected(globalRegion);
        }
      })
      .finally(() => {
        if (initGuard.current && !hasClosed.current) {
          setPhase("selecting");
        }
      });

    const timeout = setTimeout(() => {
      if (initGuard.current && !hasClosed.current && phase === "detecting") {
        setDetected(globalRegion);
        setSelected(globalRegion);
        setPhase("selecting");
      }
    }, 4000);

    return () => clearTimeout(timeout);
  }, [onComplete, forceOpen]);

  const finalize = useCallback(() => {
    if (hasClosed.current) return;
    hasClosed.current = true;
    setPhase("done");
    window.dispatchEvent(new CustomEvent("openlab-region-changed"));
    onComplete();
  }, [onComplete]);

  const handleGoToSelected = useCallback(async () => {
    if (!selected) return;
    const effectiveLang = getEffectiveLang(selected.lang);
    sessionStorage.setItem(
      "openlab-region",
      JSON.stringify({ code: selected.code, lang: selected.lang, effectiveLang })
    );
    await i18n.changeLanguage(effectiveLang);
    finalize();
  }, [selected, finalize]);

  const handleStayGlobal = useCallback(async () => {
    const effectiveLang = getEffectiveLang(globalRegion.lang);
    sessionStorage.setItem(
      "openlab-region",
      JSON.stringify({ code: globalRegion.code, lang: globalRegion.lang, effectiveLang })
    );
    await i18n.changeLanguage(effectiveLang);
    finalize();
  }, [finalize]);

  // ---- RENDER: Detecting phase ----
  if (phase === "detecting") {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-background-950/80 backdrop-blur-sm" />
        <div className="relative z-10 flex flex-col items-center gap-6 animate-fade-in-up">
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full border-2 border-primary-400/30 animate-ping" style={{ animationDuration: "2s" }} />
            <div className="absolute inset-2 rounded-full border border-primary-400/40 animate-pulse-glow" />
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="ri-radar-line text-3xl text-primary-300 animate-pulse-glow" />
            </div>
          </div>
          <p className="text-sm text-foreground-300/70 font-label tracking-wider uppercase animate-pulse">
            {t("region.detecting")}
          </p>
        </div>
      </div>
    );
  }

  if (phase === "done") return null;

  // ---- RENDER: Selecting phase ----
  const grouped = getRegionsByContinent();
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (region: RegionData) =>
    !normalizedSearch ||
    region.name.toLowerCase().includes(normalizedSearch) ||
    region.siteName.toLowerCase().includes(normalizedSearch) ||
    region.code.toLowerCase().includes(normalizedSearch);
  const filteredContinents = continentOrder.filter((continent) =>
    (grouped[continent] || []).some(matchesSearch)
  );
  const titleRegionName = detected?.siteName || globalRegion.siteName;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in-up">
      {/* Dark backdrop with world map bg */}
      <div
        className="absolute inset-0 bg-background-950/90 backdrop-blur-md transition-all duration-700"
        onClick={handleStayGlobal}
      />

      {/* World map background image */}
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: `url(https://readdy.ai/api/search-image?query=Dark%20futuristic%20world%20map%20with%20glowing%20cyan%20connection%20lines%2C%20digital%20globe%20grid%2C%20cybersecurity%20network%20topology%2C%20dark%20background%2C%20minimal%20tech%20aesthetic%2C%20high%20detail%20render&width=1600&height=900&seq=world-map-dark&orientation=landscape)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Modal - Horizontal style like Seidor */}
      <div className="relative z-10 w-full max-w-2xl mx-4">
        <div className="glass-card border border-primary-400/10 overflow-hidden shadow-2xl shadow-primary-500/5 rounded-2xl">
          {/* Top gradient line */}
          <div className="h-[3px] bg-gradient-to-r from-transparent via-primary-400/50 to-transparent" />

          <div className="px-8 py-10 md:px-12 md:py-12 text-center relative">
            {/* Corner decorative brackets */}
            <div className="absolute top-5 left-5 w-6 h-6 border-t border-l border-primary-400/20" />
            <div className="absolute top-5 right-5 w-6 h-6 border-t border-r border-primary-400/20" />
            <div className="absolute bottom-5 left-5 w-6 h-6 border-b border-l border-primary-400/20" />
            <div className="absolute bottom-5 right-5 w-6 h-6 border-b border-r border-primary-400/20" />

            {/* Globe icon */}
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500/8 border border-primary-400/15 mb-5">
              <i className="ri-earth-line text-2xl text-primary-300" />
            </div>

            {/* Title */}
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-3 leading-tight">
              {t("region.hello")}{" "}
              <span className="text-primary-300">{titleRegionName}.</span>
            </h2>

            {/* Subtitle */}
            <p className="text-sm text-foreground-300/70 mb-8 leading-relaxed max-w-md mx-auto">
              {t("region.continue")}
            </p>

            {/* Region label */}
            <div className="text-left max-w-md mx-auto mb-2">
              <label className="text-xs font-label font-semibold text-foreground-400/60 uppercase tracking-wider">
                {t("region.region")}
              </label>
            </div>

            {/* Region trigger */}
            <div className="relative max-w-md mx-auto mb-8">
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setPickerOpen(true);
                }}
                className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-background-900/70 border border-primary-400/15 text-left hover:border-primary-400/30 transition-all duration-300 cursor-pointer"
              >
                <span className="flex items-center gap-3 text-sm font-medium text-foreground-100">
                  <span className="text-lg">{selected?.flag}</span>
                  {selected?.name}
                  {selected != null && selected.code === detected?.code && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-500/15 text-accent-300 text-[9px] font-label font-semibold uppercase tracking-wider">
                      <span className="w-1 h-1 rounded-full bg-accent-400" />
                      {t("region.detected")}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-label uppercase tracking-wider text-foreground-400/50">
                    {t("region.changeSelection")}
                  </span>
                  <i className="ri-arrow-down-s-line text-lg text-foreground-400/60" />
                </span>
              </button>
            </div>

            {/* Region picker modal - rendered through a portal so it is never clipped */}
            {pickerOpen &&
              createPortal(
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 animate-fade-in">
                  <div
                    className="absolute inset-0 bg-background-950/85 backdrop-blur-md"
                    onClick={() => setPickerOpen(false)}
                  />

                  <div className="relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col glass-card border border-primary-400/12 rounded-2xl overflow-hidden animate-fade-in-up">
                    <div className="h-[3px] bg-gradient-to-r from-transparent via-primary-400/50 to-transparent" />

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 pt-6 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/8 border border-primary-400/15">
                          <i className="ri-earth-line text-lg text-primary-300" />
                        </div>
                        <h3 className="font-heading font-bold text-base md:text-lg text-foreground-50 leading-tight">
                          {t("region.selectCountry")}
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPickerOpen(false)}
                        aria-label={t("region.close")}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-foreground-100/8 text-foreground-400 hover:text-foreground-100 hover:border-primary-400/25 transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-close-line text-lg" />
                      </button>
                    </div>

                    {/* Search */}
                    <div className="px-6 pb-4">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400/60">
                          <i className="ri-search-line text-base" />
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("region.searchPlaceholder")}
                          className="w-full pl-11 pr-4 py-3 rounded-xl bg-background-900/70 border border-primary-400/15 text-sm text-foreground-100 placeholder:text-foreground-400/50 focus:outline-none focus:border-primary-400/40 transition-all duration-300"
                        />
                      </div>
                    </div>

                    {/* Scrollable list */}
                    <div className="flex-1 overflow-y-auto px-6 pb-6">
                      {filteredContinents.length === 0 ? (
                        <p className="py-10 text-center text-sm text-foreground-400/60">
                          {t("region.noResults")}
                        </p>
                      ) : (
                        filteredContinents.map((continent) => {
                          const continentRegions = (grouped[continent] || []).filter(matchesSearch);
                          if (continentRegions.length === 0) return null;
                          return (
                            <div key={continent} className="mb-4 last:mb-0">
                              <p className="text-[10px] font-label font-semibold text-foreground-400/45 uppercase tracking-wider mb-2">
                                {t(`continent.${continent}`) || continentNames[continent] || continent}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {continentRegions.map((region) => (
                                  <button
                                    key={region.code}
                                    type="button"
                                    onClick={() => {
                                      setSelected(region);
                                      setPickerOpen(false);
                                    }}
                                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm border transition-all duration-200 cursor-pointer ${
                                      selected?.code === region.code
                                        ? "text-primary-300 bg-primary-500/8 border-primary-400/25"
                                        : "text-foreground-200/75 border-transparent hover:bg-primary-500/6 hover:border-primary-400/12"
                                    }`}
                                  >
                                    <span className="text-lg">{region.flag}</span>
                                    <span className="font-medium truncate flex-1">{region.name}</span>
                                    {detected != null && detected.code === region.code && (
                                      <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent-500/15 text-accent-300 text-[9px] font-label font-semibold uppercase tracking-wider">
                                        <span className="w-1 h-1 rounded-full bg-accent-400" />
                                        {t("region.detected")}
                                      </span>
                                    )}
                                    <span className="text-[10px] font-label text-foreground-400/40 uppercase shrink-0">
                                      {langLabelMap[region.lang] || region.lang.toUpperCase()}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )}

            {/* Buttons - Horizontal like Seidor */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <button
                type="button"
                onClick={handleGoToSelected}
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-foreground-50 font-label font-semibold text-sm hover:from-primary-400 hover:to-primary-500 glow-primary transition-all duration-500 cursor-pointer whitespace-nowrap"
              >
                {t("region.goTo")} {selected?.name}
              </button>
              <button
                type="button"
                onClick={handleStayGlobal}
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-foreground-100/8 bg-foreground-100/3 text-foreground-200 font-label font-semibold text-sm hover:border-primary-400/25 hover:bg-primary-500/4 hover:text-primary-300 transition-all duration-500 cursor-pointer whitespace-nowrap"
              >
                {t("region.stayIn")} Global
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}